import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";

import { COLORS, PROP_DEFINITIONS } from "./dioramaLayout";
import { InteractiveProp } from "./InteractiveProp";

const DEF = PROP_DEFINITIONS.find((p) => p.id === "telephone")!;

/** Visual-only telephone — reusable outside the engine's InteractiveProp wrapper. */
export function TelephoneModel() {
  const handsetRef = useRef<THREE.Group>(null);
  const hoveredRef = useRef(false);

  useFrame(({ clock }) => {
    if (!handsetRef.current) return;
    if (hoveredRef.current) {
      handsetRef.current.rotation.z = Math.sin(clock.elapsedTime * 20) * 0.03;
    } else {
      handsetRef.current.rotation.z = 0;
    }
  });

  // Coiled cord positions
  const coils = Array.from({ length: 8 }, (_, i) => ({
    key: i,
    position: [0.5 + i * 0.06, 0.15 - i * 0.01, 0.2] as [number, number, number],
  }));

  return (
    <group
      onPointerEnter={() => { hoveredRef.current = true; }}
      onPointerLeave={() => { hoveredRef.current = false; }}
    >
        {/* Phone base */}
        <mesh position={[0, 0.125, 0]} castShadow>
          <boxGeometry args={[0.8, 0.25, 0.5]} />
          <meshStandardMaterial color={COLORS.coral1} flatShading />
        </mesh>

        {/* Dial area */}
        <mesh position={[0, 0.28, 0]}>
          <cylinderGeometry args={[0.18, 0.18, 0.05, 12]} />
          <meshStandardMaterial color={COLORS.cream} flatShading />
        </mesh>

        {/* Dial center */}
        <mesh position={[0, 0.3, 0]}>
          <cylinderGeometry args={[0.08, 0.08, 0.06, 8]} />
          <meshStandardMaterial color="#333333" flatShading />
        </mesh>

        {/* Cradle posts */}
        <mesh position={[-0.3, 0.32, 0]}>
          <boxGeometry args={[0.08, 0.15, 0.08]} />
          <meshStandardMaterial color={COLORS.coral1} flatShading />
        </mesh>
        <mesh position={[0.3, 0.32, 0]}>
          <boxGeometry args={[0.08, 0.15, 0.08]} />
          <meshStandardMaterial color={COLORS.coral1} flatShading />
        </mesh>

        {/* Handset */}
        <group ref={handsetRef} position={[0, 0.42, 0]}>
          <mesh>
            <boxGeometry args={[0.7, 0.08, 0.1]} />
            <meshStandardMaterial color="#333333" flatShading />
          </mesh>
          {/* Earpiece */}
          <mesh position={[-0.3, -0.02, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.1, 0.12, 0.15, 6]} />
            <meshStandardMaterial color="#333333" flatShading />
          </mesh>
          {/* Mouthpiece */}
          <mesh position={[0.3, -0.02, 0]} rotation={[0, 0, Math.PI / 2]}>
            <cylinderGeometry args={[0.1, 0.12, 0.15, 6]} />
            <meshStandardMaterial color="#333333" flatShading />
          </mesh>
        </group>

      {/* Coiled cord */}
      {coils.map((coil) => (
        <mesh key={coil.key} position={coil.position} rotation={[0, Math.PI / 2, 0]}>
          <torusGeometry args={[0.04, 0.015, 4, 6]} />
          <meshStandardMaterial color="#333333" flatShading />
        </mesh>
      ))}
    </group>
  );
}

export function OfficeTelephone() {
  return (
    <InteractiveProp
      propId={DEF.id}
      label={DEF.label}
      clickMessage={DEF.clickMessage}
      position={DEF.position}
    >
      <TelephoneModel />
    </InteractiveProp>
  );
}
